import { Injectable, AfterViewInit, NgZone } from '@angular/core';
import anime from 'animejs';

declare var particlesJS: any;
declare var pJS: any;
declare var skrollr: any;
declare var Atropos: any;
declare var Swiper: any;

@Injectable({
  providedIn: 'root'
})
export class DynamicEffectsService {

  private skrollerInstance: any;
  private atroposInstances: any[] = [];
  private particlesInstances: any[] = [];

  constructor(private ngZone: NgZone) { }

  // Initialize all dynamic effects
  initAllEffects(): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.initParticles();
        this.initSkrollr();
        this.initAtropos();
        this.initParallaxImages();
        this.initCustomCursor();
        this.initHoverEffects();
        this.initScrollAnimations();
      }, 100);
    });
  }

  // Initialize Particles.js
  initParticles(): void {
    const particleItems = document.querySelectorAll('[data-particle="true"]');
    if (particleItems.length) {
      // Wait a bit for particles.js to load
      setTimeout(() => {
        particleItems.forEach((particleItem: Element) => {
          const particleId = particleItem.getAttribute('id');
          const particleOptionsAttr = particleItem.getAttribute('data-particle-options');
          
          if (particleId && particleOptionsAttr) {
            try {
              // Create canvas element if it doesn't exist
              if (!particleItem.querySelector('.particles-js-canvas-el')) {
                const canvas = document.createElement('canvas');
                canvas.className = 'particles-js-canvas-el';
                particleItem.appendChild(canvas);
              }
              
              const particleOptions = JSON.parse(particleOptionsAttr);
              
              // Check if particlesJS is available (from particles.js library)
              const particlesJSFunc = (window as any).particlesJS || particlesJS;
              if (typeof particlesJSFunc === 'function') {
                particlesJSFunc(particleId, particleOptions);
                this.particlesInstances.push(particleId);
              } else {
                console.warn('particlesJS not loaded yet, retrying...');
                // Retry after a delay
                setTimeout(() => this.initParticles(), 500);
              }
            } catch (e) {
              console.error('Error initializing particles:', e);
            }
          }
        });
      }, 200);
    }
  }

  // Initialize Skrollr for parallax scrolling
  initSkrollr(): void {
    if (window.innerWidth >= 1200) {
      // Wait for skrollr to load
      setTimeout(() => {
        const skrollrFunc = (window as any).skrollr || skrollr;
        if (typeof skrollrFunc !== 'undefined' && skrollrFunc.init) {
          this.skrollerInstance = skrollrFunc.init({
            forceHeight: false,
            smoothScrollingDuration: 1000,
            mobileCheck: function() {
              return false;
            }
          });
        } else {
          console.warn('skrollr not loaded yet, retrying...');
          setTimeout(() => this.initSkrollr(), 500);
        }
      }, 200);
    }
  }

  // Initialize Atropos 3D tilt effects
  initAtropos(): void {
    if (typeof Atropos !== 'undefined' && window.innerWidth > 1199) {
      const atroposItems = document.querySelectorAll('[data-atropos]');
      atroposItems.forEach((item: Element) => {
        try {
          const atroposInstance = new Atropos({
            el: item
          });
          this.atroposInstances.push(atroposInstance);
        } catch (e) {
          console.error('Error initializing Atropos:', e);
        }
      });
    }
  }

  // Initialize parallax images (data-bottom-top, data-top-bottom)
  initParallaxImages(): void {
    const parallaxImages = document.querySelectorAll('[data-bottom-top], [data-top-bottom]');
    if (parallaxImages.length && window.innerWidth >= 1200) {
      let ticking = false;
      const updateParallax = () => {
        this.updateParallaxImages(parallaxImages);
        ticking = false;
      };
      
      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(updateParallax);
          ticking = true;
        }
      });
      this.updateParallaxImages(parallaxImages);
    }
  }

  private updateParallaxImages(images: NodeListOf<Element>): void {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    images.forEach((img: Element) => {
      const rect = img.getBoundingClientRect();
      const elementTop = rect.top + scrollTop;
      const elementBottom = elementTop + rect.height;
      const viewportTop = scrollTop;
      const viewportBottom = scrollTop + windowHeight;
      
      // Calculate when element enters viewport (data-bottom-top)
      const bottomTop = img.getAttribute('data-bottom-top');
      if (bottomTop) {
        // When bottom of viewport reaches top of element
        const progress = Math.max(0, Math.min(1, (viewportBottom - elementTop) / (windowHeight + rect.height)));
        const transform = this.parseTransform(bottomTop, progress);
        (img as HTMLElement).style.transform = transform;
      }
      
      // Calculate when element leaves viewport (data-top-bottom)
      const topBottom = img.getAttribute('data-top-bottom');
      if (topBottom) {
        // When top of viewport reaches bottom of element
        const progress = Math.max(0, Math.min(1, (viewportTop - elementTop + windowHeight) / (windowHeight + rect.height)));
        const transform = this.parseTransform(topBottom, progress);
        if (bottomTop) {
          // Combine both transforms
          const bottomTransform = this.parseTransform(bottomTop, 1 - progress);
          (img as HTMLElement).style.transform = `${transform} ${bottomTransform}`;
        } else {
          (img as HTMLElement).style.transform = transform;
        }
      }
    });
  }

  private parseTransform(transformString: string, progress: number): string {
    // Parse transform strings like "transform: translateY(-150px)" or "transform: translateY(150px)"
    const match = transformString.match(/translateY\(([^)]+)\)/);
    if (match) {
      const value = match[1];
      const numMatch = value.match(/(-?\d+)/);
      if (numMatch) {
        const numValue = parseInt(numMatch[1]);
        // Interpolate between 0 and the target value based on progress
        const newValue = numValue * progress;
        return `translateY(${newValue}px)`;
      }
    }
    return transformString.replace(/transform:\s*/, '');
  }

  // Initialize custom cursor
  initCustomCursor(): void {
    if (document.body.classList.contains('custom-cursor')) {
      const cursorInner = document.querySelector('.circle-cursor-inner');
      const cursorOuter = document.querySelector('.circle-cursor-outer');
      
      if (!cursorInner || !cursorOuter) {
        return;
      }

      let magneticFlag = false;
      let anchorHover = false;

      // Mouse move
      document.addEventListener('mousemove', (e: MouseEvent) => {
        if (!magneticFlag) {
          (cursorOuter as HTMLElement).style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
        }
        (cursorInner as HTMLElement).style.opacity = '1';
        (cursorInner as HTMLElement).style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      });

      // Link hover
      document.querySelectorAll('a').forEach(link => {
        link.addEventListener('mouseenter', () => {
          cursorInner.classList.add('cursor-link-hover');
          cursorOuter.classList.add('cursor-link-hover');
          anchorHover = true;
        });
        
        link.addEventListener('mouseleave', () => {
          cursorInner.classList.remove('cursor-link-hover');
          cursorOuter.classList.remove('cursor-link-hover');
          anchorHover = false;
        });
      });

      // Rounded box hover
      document.querySelectorAll('.rounded-box').forEach(box => {
        box.addEventListener('mouseenter', () => {
          const rect = (box as HTMLElement).getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          
          (cursorOuter as HTMLElement).style.transition = 'all .2s ease-out';
          (cursorOuter as HTMLElement).style.transform = `translate(${rect.left}px, ${rect.top + scrollTop}px)`;
          (cursorOuter as HTMLElement).style.width = `${rect.width}px`;
          (cursorOuter as HTMLElement).style.height = `${rect.height}px`;
          magneticFlag = true;
        });
        
        box.addEventListener('mouseleave', () => {
          (cursorOuter as HTMLElement).style.transition = '';
          (cursorOuter as HTMLElement).style.width = '';
          (cursorOuter as HTMLElement).style.height = '';
          magneticFlag = false;
        });
      });
    }
  }

  // Initialize hover effects on images/icons
  initHoverEffects(): void {
    // Portfolio hover effects (mousetip)
    const portfolioItems = document.querySelectorAll('.portfolio-box, .mousetip-wrapper');
    portfolioItems.forEach(item => {
      item.addEventListener('mousemove', (e: Event) => {
        const mouseEvent = e as MouseEvent;
        const rect = (item as HTMLElement).getBoundingClientRect();
        const mouseX = mouseEvent.clientX - rect.left + 20;
        const mouseY = mouseEvent.clientY - rect.top + 20;
        const caption = item.querySelector('.caption');
        
        if (caption) {
          (caption as HTMLElement).style.display = 'block';
          (caption as HTMLElement).style.top = `${mouseY}px`;
          (caption as HTMLElement).style.left = `${mouseX}px`;
        }
      });
      
      item.addEventListener('mouseleave', () => {
        const caption = item.querySelector('.caption');
        if (caption) {
          (caption as HTMLElement).style.display = 'none';
        }
      });
    });

    // Image hover tilt effect (slight movement on hover)
    const hoverImages = document.querySelectorAll('.portfolio-image img, .product-image, img[data-bottom-top], img[data-top-bottom]');
    hoverImages.forEach(img => {
      img.addEventListener('mouseenter', () => {
        (img as HTMLElement).style.transition = 'transform 0.3s ease';
      });
      
      img.addEventListener('mousemove', (e: Event) => {
        const mouseEvent = e as MouseEvent;
        const rect = (img as HTMLElement).getBoundingClientRect();
        const x = mouseEvent.clientX - rect.left;
        const y = mouseEvent.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const rotateX = (y - centerY) / 15; // Reduced for subtle effect
        const rotateY = (centerX - x) / 15;
        const translateY = (y - centerY) / 30; // Slight vertical movement
        
        (img as HTMLElement).style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(${translateY}px) scale(1.02)`;
      });
      
      img.addEventListener('mouseleave', () => {
        (img as HTMLElement).style.transform = '';
        (img as HTMLElement).style.transition = '';
      });
    });

    // Floating animation for elements with animation-float class
    const floatElements = document.querySelectorAll('.animation-float');
    floatElements.forEach(element => {
      this.initFloatAnimation(element);
    });
  }

  private initFloatAnimation(element: Element): void {
    let animationId: number | null = null;
    let startTime = Date.now();
    const duration = 3000; // 3 seconds for one cycle
    const amplitude = 20; // pixels to move
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = (elapsed % duration) / duration;
      const offset = Math.sin(progress * Math.PI * 2) * amplitude;
      
      (element as HTMLElement).style.transform = `translateY(${offset}px)`;
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    
    // Store animation ID for cleanup if needed
    (element as any).__floatAnimationId = animationId;
  }

  // Initialize scroll-triggered animations
  initScrollAnimations(): void {
    const animatedElements = document.querySelectorAll('[data-anime]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const animeData = element.getAttribute('data-anime');
          
          if (animeData) {
            try {
              const options = JSON.parse(animeData);
              this.animateElement(element, options);
              observer.unobserve(element);
            } catch (e) {
              console.error('Error parsing anime data:', e);
            }
          }
        }
      });
    }, { threshold: 0.1 });

    animatedElements.forEach(el => observer.observe(el));
  }

  private animateElement(element: Element, options: any): void {
    if (options.el === 'childs') {
      const children = Array.from(element.children);
      children.forEach((child: Element, index: number) => {
        const delay = (options.delay || 0) + (index * (options.staggervalue || 0));
        anime({
          targets: child,
          translateY: options.translateY || [30, 0],
          opacity: options.opacity || [0, 1],
          duration: options.duration || 600,
          delay: delay,
          easing: options.easing || 'easeOutQuad'
        });
      });
    } else {
      anime({
        targets: element,
        ...options
      });
    }
  }

  // Cleanup on destroy
  destroy(): void {
    if (this.skrollerInstance) {
      this.skrollerInstance.destroy();
    }
    
    this.atroposInstances.forEach(instance => {
      if (instance && instance.destroy) {
        instance.destroy();
      }
    });
    
    this.atroposInstances = [];
    this.particlesInstances = [];
  }
}

